import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    AccountBalanceRounded,
    CheckRounded,
    CloseRounded,
    ContentCopyRounded,
    PaymentsRounded,
} from "@mui/icons-material";
import { BANK_TRANSFER_DETAILS, BILLING_CYCLES, CURRENCY, PAYMENT_METHODS } from "../../constants/membership";
import { colors } from "../../theme/colors";
import BackdropOverlay from "../common/BackdropOverlay.jsx";
import { DIALOG_PAPER_RADIUS } from "../common/dialogActionButtonSx";
import Tooltip from "../common/Tooltip";

const DIALOG_SX = {
    "@keyframes subscriptionCheckoutIn": {
        "0%": { opacity: 0, transform: "translateY(-6px)" },
        "100%": { opacity: 1, transform: "translateY(0)" },
    },
    position: "relative",
    width: { xs: "calc(100vw - 2rem)", sm: "31rem" },
    maxWidth: "31rem",
    maxHeight: { xs: "calc(100vh - 2rem)", sm: "calc(100vh - 4rem)" },
    overflowY: "auto",
    overscrollBehavior: "contain",
    boxSizing: "border-box",
    color: colors.text.primary,
    background: `
        linear-gradient(
            165deg,
            rgba(18,14,14,0.99) 0%,
            rgba(10,10,12,1) 48%,
            rgba(8,8,10,1) 100%
        )
    `,
    border: `1px solid ${colors.border.default}`,
    borderRadius: DIALOG_PAPER_RADIUS,
    boxShadow: "0 28px 64px rgba(0, 0, 0, 0.55)",
    animation: "subscriptionCheckoutIn 0.2s ease-out",
    "&::before": {
        content: '""',
        position: "absolute",
        zIndex: 2,
        top: 0,
        left: "12%",
        right: "12%",
        height: "1px",
        background: `linear-gradient(90deg, transparent, ${alpha(colors.accent.main, 0.55)}, transparent)`,
        pointerEvents: "none",
    },
};

const CLOSE_SX = {
    position: "absolute",
    zIndex: 3,
    top: "0.8rem",
    right: "0.8rem",
    width: "1.9rem",
    height: "1.9rem",
    borderRadius: "0.55rem",
    color: colors.text.secondary,
    backgroundColor: "rgba(255,255,255,0.04)",
    "& svg": { fontSize: "0.95rem" },
    "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
};

const LABEL_SX = {
    mb: "0.65rem",
    color: alpha(colors.text.secondary, 0.66),
    fontSize: "0.6rem",
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
};

const CYCLE_SX = {
    position: "relative",
    display: "flex",
    width: { xs: "13.5rem", sm: "100%" },
    maxWidth: "100%",
    mx: { xs: "auto", sm: 0 },
    height: "2.5rem",
    p: "3px",
    mb: "1.1rem",
    boxSizing: "border-box",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "5px",
    background: "linear-gradient(180deg, rgba(255,255,255,0.025), rgba(255,255,255,0.01))",
};

const cycleIndicatorSx = (annual) => ({
    position: "absolute",
    top: "3px",
    left: "3px",
    width: "calc(50% - 3px)",
    height: "calc(100% - 6px)",
    border: `1px solid ${alpha(colors.primary.main, 0.25)}`,
    borderRadius: "3px",
    background: `linear-gradient(180deg, ${alpha(colors.primary.main, 0.16)}, ${alpha(colors.primary.main, 0.07)})`,
    transform: annual ? "translateX(100%)" : "translateX(0)",
    transition: "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
    pointerEvents: "none",
});

const cycleButtonSx = (active) => ({
    position: "relative",
    zIndex: 1,
    flex: 1,
    minWidth: 0,
    border: 0,
    borderRadius: 0,
    background: "transparent",
    color: active ? colors.text.primary : colors.text.muted,
    font: "inherit",
    fontSize: "0.65rem",
    fontWeight: 760,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    cursor: "pointer",
    "&:focus-visible": { outline: `1px solid ${colors.primary.main}`, outlineOffset: "-3px" },
    "&:disabled": { cursor: "wait" },
});

const methodSx = (selected, disabled = false) => ({
    position: "relative",
    minWidth: 0,
    minHeight: "5.15rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "space-between",
    gap: "0.55rem",
    p: "0.78rem 0.85rem",
    border: `1px solid ${selected ? alpha(colors.primary.main, 0.5) : colors.border.default}`,
    borderRadius: "7px",
    background: selected
        ? `linear-gradient(145deg, ${alpha(colors.primary.main, 0.11)}, ${alpha(colors.primary.main, 0.035)})`
        : "rgba(255,255,255,0.018)",
    color: selected ? colors.text.primary : colors.text.secondary,
    font: "inherit",
    textAlign: "left",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.46 : 1,
    transition: "border-color 0.16s ease, background 0.16s ease, transform 0.12s ease",
    "&:hover": disabled ? {} : {
        borderColor: alpha(colors.primary.main, selected ? 0.58 : 0.28),
        backgroundColor: alpha(colors.primary.main, 0.045),
    },
    "&:active": disabled ? {} : { transform: "scale(0.985)" },
    "&:focus-visible": { outline: `2px solid ${colors.primary.main}`, outlineOffset: "2px" },
});

const PAYPAL_BUTTON_SX = {
    width: "100%",
    minHeight: "2.9rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.55rem",
    px: "1.25rem",
    border: "none",
    borderRadius: "999px",
    background: "#ffc439",
    boxShadow: "0 3px 12px rgba(0,0,0,0.3)",
    cursor: "pointer",
    transition: "filter 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease",
    "&:hover": { filter: "brightness(0.96)", boxShadow: "0 5px 16px rgba(0,0,0,0.36)" },
    "&:active": { transform: "scale(0.985)", filter: "brightness(0.92)" },
    "&:focus-visible": { outline: "2px solid #0070ba", outlineOffset: "3px" },
    "&:disabled": { cursor: "wait", opacity: 0.84 },
};

function SubscriptionCheckoutDialog({
    open,
    planName,
    renewal,
    billingCycle,
    onBillingCycleChange,
    pricing,
    founderApplied,
    bankTransferData,
    loading,
    error,
    copiedField,
    onCopy,
    onPayPal,
    onBankTransfer,
    onClose,
    formatCurrency,
}) {
    const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.PAYPAL);
    const annual = billingCycle === BILLING_CYCLES.YEARLY;
    const bankAllowed = annual;

    useEffect(() => {
        if (open) setPaymentMethod(PAYMENT_METHODS.PAYPAL);
    }, [open, billingCycle]);

    const selectBankTransfer = () => {
        if (loading || !bankAllowed) return;
        setPaymentMethod(PAYMENT_METHODS.BANK_TRANSFER);
        if (!bankTransferData) onBankTransfer();
    };

    const bankFields = [
        ["Amount", bankTransferData
            ? `${formatCurrency(bankTransferData.amount)} ${bankTransferData.currency || CURRENCY}`
            : "", "bank-amount"],
        ["Account holder", BANK_TRANSFER_DETAILS.accountHolder, "bank-holder"],
        ["IBAN", BANK_TRANSFER_DETAILS.iban, "bank-iban"],
        ["BIC / SWIFT", BANK_TRANSFER_DETAILS.bic, "bank-bic"],
        [
            "Bank name and address",
            [BANK_TRANSFER_DETAILS.bankName, BANK_TRANSFER_DETAILS.bankAddress]
                .filter(Boolean).join(", "),
            "bank-address",
        ],
        ["Payment reference", bankTransferData?.referenceCode, "bank-reference"],
    ];

    return (
        <BackdropOverlay open={open} onClick={onClose}>
            <Box
                role="dialog"
                aria-modal="true"
                aria-labelledby="subscription-checkout-title"
                sx={DIALOG_SX}
            >
                <IconButton
                    aria-label="Close payment options"
                    disabled={loading}
                    onClick={onClose}
                    sx={CLOSE_SX}
                >
                    <CloseRounded />
                </IconButton>

                <Box sx={{ px: { xs: "1.3rem", sm: "1.65rem" }, pt: { xs: "1.3rem", sm: "1.55rem" }, pb: "1.2rem", borderBottom: `1px solid ${colors.border.subtle}` }}>
                    <Typography sx={{ ...LABEL_SX, mb: "0.4rem", letterSpacing: "0.18em" }}>
                        {renewal ? "Renew" : "Upgrade"}
                    </Typography>
                    <Typography id="subscription-checkout-title" component="h2" sx={{ pr: "2.5rem", color: colors.text.primary, fontSize: { xs: "1.55rem", sm: "1.75rem" }, fontWeight: 780, lineHeight: 1.1 }}>
                        {planName || "Membership"}
                    </Typography>
                    <Typography sx={{ mt: "0.28rem", color: colors.text.muted, fontSize: "0.69rem" }}>
                        Ouro membership
                    </Typography>
                </Box>

                <Box sx={{ p: { xs: "1.2rem 1.3rem 1.35rem", sm: "1.35rem 1.65rem 1.55rem" } }}>
                    <Typography sx={LABEL_SX}>Billing cycle</Typography>
                    <Box sx={CYCLE_SX} aria-label="Checkout billing cycle">
                        <Box aria-hidden sx={cycleIndicatorSx(annual)} />
                        <Box component="button" type="button" aria-pressed={!annual} disabled={loading || renewal} title={renewal ? "Yearly renewals use annual billing" : undefined} onClick={() => onBillingCycleChange(BILLING_CYCLES.MONTHLY)} sx={cycleButtonSx(!annual)}>
                            Monthly
                        </Box>
                        <Box component="button" type="button" aria-pressed={annual} disabled={loading} onClick={() => onBillingCycleChange(BILLING_CYCLES.YEARLY)} sx={cycleButtonSx(annual)}>
                            Yearly
                        </Box>
                    </Box>

                    {pricing ? (
                        <Box sx={{ px: "0.95rem", py: "0.85rem", border: `1px solid ${colors.border.subtle}`, borderRadius: "7px", background: "linear-gradient(180deg, rgba(255,255,255,0.025), rgba(255,255,255,0.012))" }}>
                            <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.45rem" }}>
                                <Typography component="span" sx={{ color: colors.text.primary, fontSize: { xs: "1.8rem", sm: "2rem" }, fontWeight: 780, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                                    {formatCurrency(pricing.monthlyAmount)}
                                </Typography>
                                <Typography component="span" sx={{ color: colors.text.muted, fontSize: "0.7rem", fontWeight: 550 }}>
                                    / month
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", mt: "0.45rem" }}>
                                <Typography sx={{ color: colors.text.muted, fontSize: "0.67rem", lineHeight: 1.35 }}>
                                    {annual ? `Billed ${formatCurrency(pricing.yearlyTotal)} annually` : "Billed monthly"}
                                </Typography>
                                {founderApplied ? (
                                    <Box component="span" sx={{ flexShrink: 0, px: "0.48rem", py: "0.25rem", border: `1px solid ${alpha(colors.accent.main, 0.22)}`, borderRadius: "3px", backgroundColor: alpha(colors.accent.main, 0.07), color: colors.accent.main, fontSize: "0.55rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                        Founder rate
                                    </Box>
                                ) : null}
                            </Box>
                        </Box>
                    ) : null}

                    <Box sx={{ height: "1px", my: "1.15rem", background: `linear-gradient(90deg, transparent, ${colors.border.default} 10%, ${colors.border.default} 90%, transparent)` }} />
                    <Typography sx={LABEL_SX}>Payment method</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "0.65rem", mb: "1rem" }}>
                        <Box component="button" type="button" aria-pressed={paymentMethod === PAYMENT_METHODS.PAYPAL} disabled={loading} onClick={() => setPaymentMethod(PAYMENT_METHODS.PAYPAL)} sx={methodSx(paymentMethod === PAYMENT_METHODS.PAYPAL)}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", color: paymentMethod === PAYMENT_METHODS.PAYPAL ? colors.accent.main : colors.text.secondary }}>
                                <PaymentsRounded sx={{ fontSize: "1.2rem" }} />
                                <CheckRounded sx={{ fontSize: "0.95rem", opacity: paymentMethod === PAYMENT_METHODS.PAYPAL ? 1 : 0 }} />
                            </Box>
                            <Typography component="span" sx={{ color: "inherit", fontSize: "0.75rem", fontWeight: 720 }}>PayPal</Typography>
                        </Box>
                        <Box component="button" type="button" aria-pressed={paymentMethod === PAYMENT_METHODS.BANK_TRANSFER} aria-label={`Bank transfer${bankAllowed ? "" : " available for yearly billing only"}`} disabled={loading || !bankAllowed} onClick={selectBankTransfer} sx={methodSx(paymentMethod === PAYMENT_METHODS.BANK_TRANSFER, !bankAllowed)}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.35rem", color: paymentMethod === PAYMENT_METHODS.BANK_TRANSFER ? colors.accent.main : colors.text.secondary }}>
                                <AccountBalanceRounded sx={{ fontSize: "1.2rem", flexShrink: 0 }} />
                                {!bankAllowed ? (
                                    <Typography
                                        component="span"
                                        sx={{
                                            color: colors.text.muted,
                                            fontSize: { xs: "0.48rem", sm: "0.52rem" },
                                            fontWeight: 700,
                                            letterSpacing: "0.05em",
                                            textTransform: "uppercase",
                                            lineHeight: 1.2,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        Yearly only
                                    </Typography>
                                ) : (
                                    <CheckRounded sx={{ fontSize: "0.95rem", opacity: paymentMethod === PAYMENT_METHODS.BANK_TRANSFER ? 1 : 0 }} />
                                )}
                            </Box>
                            <Typography component="span" sx={{ color: "inherit", fontSize: "0.75rem", fontWeight: 720 }}>Bank transfer</Typography>
                        </Box>
                    </Box>

                    {error ? <Alert severity="error" sx={{ mb: "1rem" }}>{error}</Alert> : null}

                    {paymentMethod === PAYMENT_METHODS.PAYPAL ? (
                        <Box>
                            <Typography sx={{ mb: "0.85rem", color: colors.text.secondary, fontSize: "0.7rem", lineHeight: 1.55 }}>
                                You’ll continue to PayPal to approve the secure payment and return to Ouro when it’s complete.
                            </Typography>
                            <Box component="button" type="button" aria-label="Continue with PayPal" disabled={loading} onClick={onPayPal} sx={PAYPAL_BUTTON_SX}>
                                {loading ? (
                                    <>
                                        <CircularProgress size={19} sx={{ color: "#003087" }} />
                                        <Box component="span" sx={{ color: "#003087", fontFamily: "Arial, Helvetica, sans-serif", fontSize: "0.82rem", fontWeight: 700 }}>Connecting…</Box>
                                    </>
                                ) : (
                                    <Box component="span" sx={{ display: "flex", alignItems: "baseline", fontFamily: "Arial, Helvetica, sans-serif", fontSize: "1.28rem", fontWeight: 800, fontStyle: "italic", letterSpacing: "-0.045em", lineHeight: 1, userSelect: "none" }}>
                                        <Box component="span" sx={{ color: "#003087" }}>Pay</Box>
                                        <Box component="span" sx={{ color: "#009cde", ml: "-1px" }}>Pal</Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Box>
                            {loading ? (
                                <Box sx={{ minHeight: "4.5rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.7rem", color: colors.text.secondary, fontSize: "0.72rem", fontWeight: 600 }} role="status">
                                    <CircularProgress size={21} sx={{ color: colors.primary.main }} />
                                    Preparing your transfer details…
                                </Box>
                            ) : bankTransferData ? (
                                <>
                                    <Typography sx={{ mb: "0.85rem", color: colors.text.secondary, fontSize: "0.7rem", lineHeight: 1.55 }}>
                                        Transfer the exact amount below and include the reference so we can match your payment.
                                    </Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                        {bankFields.map(([label, value, id]) => (
                                            <Box key={label} sx={{ minWidth: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", px: "0.8rem", py: "0.68rem", border: `1px solid ${colors.border.subtle}`, borderRadius: "6px", background: "rgba(255,255,255,0.018)" }}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ mb: "0.25rem", color: colors.text.muted, fontSize: "0.55rem", fontWeight: 750, letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</Typography>
                                                    <Typography sx={{ color: label === "Payment reference" ? colors.accent.main : colors.text.primary, fontFamily: label === "IBAN" || label === "BIC / SWIFT" || label === "Payment reference" ? "'JetBrains Mono', ui-monospace, monospace" : "inherit", fontSize: "0.7rem", fontWeight: 650, lineHeight: 1.35, overflowWrap: "anywhere" }}>
                                                        {value || "-"}
                                                    </Typography>
                                                </Box>
                                                <Tooltip title={`Copy ${label.toLowerCase()}`} placement="top">
                                                    <Box component="button" type="button" aria-label={`Copy ${label.toLowerCase()}`} disabled={!value} onClick={() => onCopy(value, id)} sx={{ flexShrink: 0, width: "2rem", height: "2rem", display: "inline-flex", alignItems: "center", justifyContent: "center", p: 0, border: `1px solid ${alpha(colors.primary.main, 0.2)}`, borderRadius: "5px", background: "transparent", color: copiedField === id ? colors.state.success.main : colors.primary.main, cursor: "pointer", "&:hover": { backgroundColor: alpha(colors.primary.main, 0.08) }, "&:disabled": { opacity: 0.4, cursor: "default" }, "& svg": { fontSize: "1rem" } }}>
                                                        {copiedField === id ? <CheckRounded /> : <ContentCopyRounded />}
                                                    </Box>
                                                </Tooltip>
                                            </Box>
                                        ))}
                                    </Box>
                                    <Typography sx={{ mt: "0.75rem", color: colors.text.muted, fontSize: "0.64rem", lineHeight: 1.5 }}>
                                        Membership activates after confirmation, usually within 1–3 business days.
                                    </Typography>
                                </>
                            ) : (
                                <Box component="button" type="button" onClick={onBankTransfer} sx={{ width: "100%", minHeight: "2.65rem", border: `1px solid ${alpha(colors.primary.main, 0.3)}`, borderRadius: "6px", background: alpha(colors.primary.main, 0.07), color: colors.accent.main, font: "inherit", fontSize: "0.69rem", fontWeight: 750, letterSpacing: "0.05em", textTransform: "uppercase", cursor: "pointer", "&:hover": { background: alpha(colors.primary.main, 0.11) } }}>
                                    Prepare bank transfer details
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </BackdropOverlay>
    );
}

SubscriptionCheckoutDialog.propTypes = {
    open: PropTypes.bool,
    planName: PropTypes.string,
    renewal: PropTypes.bool,
    billingCycle: PropTypes.string,
    onBillingCycleChange: PropTypes.func.isRequired,
    pricing: PropTypes.object,
    founderApplied: PropTypes.bool,
    bankTransferData: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.string,
    copiedField: PropTypes.string,
    onCopy: PropTypes.func.isRequired,
    onPayPal: PropTypes.func.isRequired,
    onBankTransfer: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    formatCurrency: PropTypes.func.isRequired,
};

export default SubscriptionCheckoutDialog;
